/**
 * Number and time formatting of the token pages. Pure, no React: the components render the parts.
 * A price below 0.001 is shown as 0.0{n}1234 (n leading zeros) because a row of zeros is unreadable on a phone.
 */
export { fmtUsd } from 'views/TradingCompetitionV3/pool-search/logic'

export type PriceParts =
  | { kind: 'none' }
  | { kind: 'plain'; text: string }
  | { kind: 'sub'; lead: string; zeros: number; digits: string }

const group = (n: number, digits: number) => n.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })

export function priceParts(n: number | null | undefined): PriceParts {
  if (n === null || n === undefined || !Number.isFinite(n) || n <= 0) return { kind: 'none' }
  if (n >= 1000) return { kind: 'plain', text: group(n, 2) }
  if (n >= 1) return { kind: 'plain', text: group(n, n >= 100 ? 2 : 4).replace(/(\.\d*?[1-9])0+$|\.0+$/, '$1') }
  if (n >= 0.001) return { kind: 'plain', text: Number(n.toPrecision(4)).toString() }
  const zeros = -Math.floor(Math.log10(n)) - 1 // leading zeros after the decimal point
  if (zeros > 30) return { kind: 'plain', text: '0' }
  const digits = Math.round(n * 10 ** (zeros + 4)).toString().padStart(4, '0').slice(0, 4)
  return { kind: 'sub', lead: '0.0', zeros, digits: digits.replace(/0+$/, '') || '0' }
}
/** The same price as plain text (tooltips, aria-labels, tests): 0.0{4}4697 means four zeros after the first one. */
export function priceText(n: number | null | undefined): string {
  const p = priceParts(n)
  if (p.kind === 'none') return '—'
  if (p.kind === 'plain') return `$${p.text}`
  return `$${p.lead}{${p.zeros}}${p.digits}`
}

export function fmtPct(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—'
  const a = Math.abs(n)
  const body = a >= 100000 ? `${(a / 1000).toFixed(0)}K` : a >= 1000 ? a.toLocaleString('en-US', { maximumFractionDigits: 0 }) : a.toFixed(2)
  if (a < 0.005) return '0.00%'
  return `${n < 0 ? '-' : '+'}${body}%`
}
export const changeTone = (n: number | null | undefined): 'up' | 'down' | 'flat' => (n === null || n === undefined || !Number.isFinite(n) || Math.abs(n) < 0.005 ? 'flat' : n > 0 ? 'up' : 'down')

export function fmtCompact(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—'
  if (n === 0) return '0'
  if (Math.abs(n) < 1000) return n.toLocaleString('en-US', { maximumFractionDigits: 2 })
  return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 2 }).format(n)
}
export const fmtInt = (n: number | null | undefined): string => (n === null || n === undefined || !Number.isFinite(n) ? '—' : Math.round(n).toLocaleString('en-US'))

/** Token amounts in a trade row: 4,677.04 · 4.68M · 0.000123 */
export function fmtAmount(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—'
  const a = Math.abs(n)
  if (a === 0) return '0'
  if (a >= 1_000_000) return fmtCompact(n)
  if (a >= 1) return n.toLocaleString('en-US', { maximumFractionDigits: 2 })
  return Number(n.toPrecision(3)).toString()
}

export const shortAddress = (a?: string | null) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : '')

/** "now", "5m", "3h", "2d" since `t` (unix seconds). */
export function fmtAgo(t: number, nowSec: number): string {
  const d = Math.max(0, nowSec - t)
  if (d < 45) return 'now'
  if (d < 3600) return `${Math.max(1, Math.round(d / 60))}m`
  if (d < 172_800) return `${Math.round(d / 3600)}h`
  return `${Math.round(d / 86_400)}d`
}
/** The age of a pool or token for a card: "3h old", "12d old". */
export function fmtAge(t: number | null | undefined, nowSec: number): string {
  if (!t) return ''
  const d = Math.max(0, nowSec - t)
  if (d < 3600) return `${Math.max(1, Math.round(d / 60))}m old`
  if (d < 172_800) return `${Math.round(d / 3600)}h old`
  if (d < 86_400 * 90) return `${Math.round(d / 86_400)}d old`
  if (d < 86_400 * 730) return `${Math.round(d / (86_400 * 30))}mo old`
  return `${Math.round(d / (86_400 * 365))}y old`
}
